import $, {Cash} from 'cash-dom';
import {
    ajaxGet,
    cError,
    getCleanUrl,
    getQueryStringToken,
    historyPush,
    historyUpdate, publish, scrollToInstant, showBrowserLoading,
    subscribe, wait
} from '../humans/helper';
import {HistoryDirection, HistoryType, InternalHistoryState} from './types/history';
import {AxiosResponse} from 'axios';
import History from './history';
import {ModalClosedEvent, ModalOpenedEvent, ModalReplacedEvent} from './types/modal';
import {createFocusTrap, FocusTrap} from 'focus-trap';

// Class used to indicate modal is open (replaces native dialog [open] attribute)
const ACTIVE_CLASS = 'is-modal-active';
const SCROLLABLE_CLASS = 'is-scrollable';
const BODY_CLASS = 'has-modal';

/**
 * Modal module
 * @author Tim Jungmann <Tim@Humans-Machines.com>
 * @author Stefan Rüschenberg <Stefan@Humans-Machines.com>
 */
export default class Modal {
    /**
     * Singleton instance of the component
     */
    static #instance: Modal;

    /**
     * Default modal size to use, if no size is defined
     */
    static readonly DEFAULT_MODAL_SIZE = 'full';

    /**
     * Number of modal levels
     */
    static readonly MODAL_LEVELS = 1;

    /**
     * Cash body reference
     */
    protected $body: Cash;

    /**
     * UI references
     */
    protected ui = {
        // General modals
        modal: '.js-modal',
        // Uses class instead of [open] attribute since we're using div instead of dialog
        activeModal: '.js-modal.is-modal-active, .js-modal-async.is-modal-active',
        modalOpenTrigger: '.js-modal-open',
        modalCloseTrigger: '.js-modal-close',
        modalCloseButton: '.modal__close-btn',
        modalParentCloseTrigger: '.js-modal-parent-close',
        searchModal: '.js-modal[data-modal-name="search"]',

        // Async modals
        asyncModal: '.js-modal-async',
        asyncModalTrigger: 'a[data-modal], button[data-modal][data-href], .text-box a[href*="/work/"]', // Add modal interaction for internal project links as well

        // Inner parts
        modalInner: '.modal__inner',
        modalContent: '.modal__content',
        modalBody: '.modal__body',
    };

    /**
     * Internal modal opened state
     */
    protected isModalOpened: boolean = false;

    /**
     * List of modal content promises
     */
    protected contentPromises: Record<string, Promise<AxiosResponse<string>>> = {};

    /**
     * Last active element before modal was opened
     */
    protected lastActiveElementStack: HTMLElement[]|Element[] = [];

    /**
     * Stack for modal windows
     */
    protected modalStack: HTMLElement[] = [];

    /**
     * Stores the modal scroll positions for the modal ids
     */
    protected modalScrollPosition: Record<string, number> = {};

    /**
     * Bound scroll position callback
     */
    protected modalScrollPositionCallback: EventListener|null = null;

    /**
     * Focus trap stack for stacked modals
     * Each modal gets its own trap; when stacking, we deactivate the previous trap
     */
    protected focusTrapStack: FocusTrap[] = [];

    /**
     * Stores the index of the last active focus trap
     * Used to restore the correct trap when lightbox (PhotoSwipe) closes
     */
    protected lastActiveFocusTrapIndex?: number | null = null;

    /**
     * Getter method to check if modal is opened
     */
    get modalOpened(): boolean {
        return this.isModalOpened || $(this.ui.activeModal).length > 0;
    }

    /**
     * Sets the modal opened state
     */
    set modalOpened(state: boolean) {
        this.isModalOpened = state;
        this.$body.toggleClass(BODY_CLASS, this.modalOpened);
    }

    /**
     * Default modal constructor
     */
    protected constructor() {
        this.$body = $('body');
        this.registerEvents();
        this.handleInitialModalState();
    }

    /**
     * Returns the singleton instance of the component
     */
    public static getInstance(): Modal {
        if (!Modal.#instance) {
            Modal.#instance = new Modal();
        }

        return Modal.#instance;
    }

    /**
     * Register required browser and PubSub events
     */
    protected registerEvents(): void {
        this.$body.on('click', this.ui.modalOpenTrigger, this.onModalOpenClick.bind(this));
        this.$body.on('click', this.ui.modalCloseTrigger, this.onModalCloseClick.bind(this));
        this.$body.on('click', this.ui.modalParentCloseTrigger, this.onModalParentCloseTrigger.bind(this));
        this.$body.on('keydown', this.onCaptureKeyup.bind(this));


        this.$body.on('click', this.ui.asyncModalTrigger, this.onAsyncModalTriggerClick.bind(this));

        subscribe('history.state.pushed', this.onHistoryStateChanged.bind(this));
        subscribe('history.state.popped', this.onHistoryStateChanged.bind(this));

        // Focus trap events - allows lightbox (PhotoSwipe) to temporarily deactivate modal's focus trap
        subscribe('modal.focustrap.activate', this.onFocustrapChangeRequest.bind(this));
        subscribe('modal.focustrap.deactivate', this.onFocustrapChangeRequest.bind(this));

        // Set bound scroll handler callback
        this.modalScrollPositionCallback = this.modalScrollPositionHandler.bind(this) as EventListener;
    }

    /**
     * Handles the initial history state if it's relevant for modals
     */
    protected handleInitialModalState(): void {
        const state = history.state || {};

        if (this.isInlineModalRelevantState(state)) {
            const $modal = this.getModalFromHash(state.hash);

            if ($modal) {
                this.openModal($modal);
            }
        } else if (this.isAsyncModalRelevantState(state)) {
            if (state.mainUrl) {
                this.loadMainContent(state.mainUrl);
            }

            // Re-open modals
            if (state.modalUrl) {
                for (let i = 0; i < Modal.MODAL_LEVELS; i++) {
                    if (state.modalUrl[i]) {
                        this.openAsyncModal(state.modalUrl[i], i + 1, state.modalSize[i] ?? Modal.DEFAULT_MODAL_SIZE);
                    }
                }
            }
        }
    }

    /**
     * Handles modal relevant history changes
     */
    protected onHistoryStateChanged(topic: string, data: Partial<InternalHistoryState>): void {
        if (this.isInlineModalRelevantPrimaryState(data, topic)) {
            this.handleInlineModalHistoryChange(data, topic);

        } else if (this.isAsyncModalRelevantPrimaryState(data, topic)) {
            this.handleAsyncModalHistoryChange(data, topic);

        } else if (this.isInlineModalRelevantState(data, topic)) {
            this.handleInlineModalHistoryChange(data, topic);

        } else if (this.isAsyncModalRelevantState(data, topic)) {
            this.handleAsyncModalHistoryChange(data, topic);
        }
    }

    /**
     * Handles history changes for inline modal windows
     */
    protected handleInlineModalHistoryChange(data: Partial<InternalHistoryState>, topic: string): void {
        if (data.hash && (topic === 'history.state.pushed' || data.direction === HistoryDirection.Forward)) {
            const $modal = this.getModalFromHash(data.hash);

            if ($modal) {
                this.openModal($modal);
            }
        } else if (data.direction === HistoryDirection.Back) {
            const $modal = this.getModalFromHash(data.previousState?.hash);

            if ($modal) {
                this.closeModal($modal);
            }
        }
    }

    /**
     * Handles history changes for async modal windows
     */
    protected handleAsyncModalHistoryChange(data: Partial<InternalHistoryState>, topic: string): void {
        if (topic === 'history.state.pushed' || data.direction === HistoryDirection.Forward) {
            if (data.url) {
                const modalLevel = data.modalLevel ?? 1;
                const modalSize = data.modalSize && data.modalSize.length >= modalLevel
                    ? data.modalSize[modalLevel - 1]
                    : Modal.DEFAULT_MODAL_SIZE;

                this.openAsyncModal(data.url, modalLevel, modalSize);
            }
        } else if (data.direction === HistoryDirection.Back) {
            if (data.type === HistoryType.ModalAsync && data.url) {
                const modalLevel = data.modalLevel ?? 1;

                // Close L2 modal
                if (data.previousState && data.previousState.modalLevel !== modalLevel) {
                    this.closeModal($(this.ui.asyncModal).filter(`[data-modal-level="${data.previousState.modalLevel}"]`));
                } else { // Open/replace modal
                    const modalSize = data.modalSize && data.modalSize.length >= modalLevel
                        ? data.modalSize[modalLevel - 1]
                        : Modal.DEFAULT_MODAL_SIZE;
                    this.openAsyncModal(data.url, modalLevel, modalSize);
                }
            } else {
                // Use class selector instead of [open] attribute
                this.closeModal($(this.ui.asyncModal).filter('.is-modal-active').last());
            }
        }
    }

    /**
     * Replaces the main content from the given URL
     */
    protected async loadMainContent(url: string): Promise<void> {
        const cleanUrl = getCleanUrl(url);

        try {
            const {data} = await ajaxGet(`${cleanUrl}${getQueryStringToken(url)}async=1`);
            const html = $(data);
            $('#main').html(html.html());

            publish('main.updated', {
                url: cleanUrl,
            });
        } catch (error) {
            cError(error)
        }
    }

    /**
     * Handles the click event on an async modal trigger
     */
    protected onAsyncModalTriggerClick(event: PointerEvent): void {
        // Ignore if the user wants to open link in new tab/window
        if (event.ctrlKey || event.shiftKey || event.metaKey) {
            return;
        }

        event.preventDefault();

        const $target = $(event.currentTarget as HTMLElement);
        const path = $target.attr('href') ?? $target.data('href');
        const level = Number($target.data('modalLevel') ?? 1);

        if (!path) {
            return;
        }

        // Push history state
        const pushHistoryCallback = () => {
            historyPush({
                type: HistoryType.ModalAsync,
                url: getCleanUrl(path),
                mainUrl: history.state.mainUrl ?? getCleanUrl(),
                modalLevel: level,
                modalUrl: this.getModalUrlState(level, path),
                modalSize: this.getModalSizeState(level, $target.data('modalSize') ?? $target.data('modalType')),
                modalHistoryCount: this.getModalHistoryCountState(level),
            });
        };

        // Special case: Modal level 2 is already opened but level 1 is requested
        const $currentModal = this.getActiveModal();
        if (level === 1 && $currentModal.data('modalLevel') === 2) {
            this.requestModalClose($currentModal);

            setTimeout(() => {
                pushHistoryCallback();
            }, 100);
        } else {
            pushHistoryCallback();
        }
    }

    /**
     * Returns the merged state array for the modal URL
     */
    protected getModalUrlState(level: number, url: string): string[] {
        const modalUrl = getCleanUrl(url);
        const state = history.state.modalUrl ?? [];
        state[level - 1] = modalUrl;

        return state;
    }

    /**
     * Returns the merged state array for the modal size
     */
    protected getModalSizeState(level: number, size?: string): string[] {
        const modalSize = size ?? Modal.DEFAULT_MODAL_SIZE;
        const state = history.state.modalSize ?? [];
        state[level - 1] = modalSize;

        return state;
    }

    /**
     * Returns the merged state array for the modal history count
     */
    protected getModalHistoryCountState(level: number): number[] {
        let count = 0;
        if (history.state?.modalHistoryCount?.length >= level) {
            count = history.state.modalHistoryCount[level - 1];
        }

        const state = history.state.modalHistoryCount ?? [];
        state[level - 1] = count + 1;

        return state;
    }

    /**
     * Opens an async modal window and loads the related content
     */
    protected async openAsyncModal(path: string, level: number = 1, size: string = 'full'): Promise<void> {
        const $modal = $(this.ui.asyncModal).filter(`[data-modal-level="${level}"]`);
        // Check if modal is already open using class instead of [open] attribute
        const isModalOpened = $modal.hasClass(ACTIVE_CLASS);

        // Mark modal as busy
        $modal.attr('aria-busy', 'true');

        if (isModalOpened) {
            this.replaceModal($modal);
        } else {
            $modal.find('.modal__body').html('');

            // Set modal size
            $modal.attr('data-modal-type', size);

            this.openModal($modal);
        }

        try {
            const {data} = await this.getAsyncModalContent(path);
            const html = $(data);
            const content = html.html();
            const browserTitle = html.data('browserTitle') ? html.data('browserTitle').trim() : document.title

            // Set/Replace content
            $modal.find('.modal__body').html(content);

            // Set modal size
            // if not opened yet or changing to fullsize modal
            // Do not downsize modal!
            // Todo: Maybe the resize should also happen for history back events
            if (!isModalOpened || size == Modal.DEFAULT_MODAL_SIZE) {
                $modal.attr('data-modal-type', size);
            }

            // Handle scroll position
            if (isModalOpened) {
                // Restore scroll position when navigating back to a previously viewed modal
                const modalUrl = history.state?.modalUrl?.[level - 1];
                const scrollKey = modalUrl ? `${level}:${modalUrl}` : null;
                const scrollPosition = scrollKey ? (this.modalScrollPosition[scrollKey] ?? 0) : 0;
                scrollToInstant($modal.get(0), {top: scrollPosition});
            } else {
                // Scroll to top for newly opened modals
                scrollToInstant($modal.get(0), {top: 0});
            }

            // Update title
            if (browserTitle.length) {
                historyUpdate({
                    title: browserTitle,
                });
            }

            publish('modal.content-loaded', {
                $modal,
                level,
            });
        } catch (error) {
            cError(error);

            // Close modal, since we have an error
            setTimeout(() => {
                history.go(-1);
            }, 10);
        } finally {
            $modal.attr('aria-busy', 'false');
        }
    }

    /**
     * Opens the modal that is passed as parameter
     * Uses class-based visibility instead of native dialog showModal() to allow z-index stacking
     */
    protected openModal($modal: Cash): void {
        const closeButton = $modal.find(this.ui.modalCloseButton).get(0);

        // Store last active element for focus restoration on close
        this.lastActiveElementStack.push(document.activeElement as HTMLElement);

        // Add modal to stack
        if ($modal.get(0)) {
            this.modalStack.push($modal.get(0) as HTMLElement);
        }

        // Reset scroll position before making modal visible
        scrollToInstant($modal.get(0), {top: 0});

        // Activate modal (replaces native showModal())
        $modal.addClass(ACTIVE_CLASS);
        this.modalOpened = true;

        // Add modal-specific body class
        if ($modal.data('modalName')) {
            this.$body.addClass(`has-modal-${$modal.data('modalName')}`);
        }

        // Delay scrollable class + focus for smooth transition
        requestAnimationFrame(() => {
            $modal.addClass(SCROLLABLE_CLASS);
            // Prevent scroll jump when focusing close button
            closeButton?.focus({preventScroll: true});
        });

        // Deactivate previous trap if stacking modals
        if (this.focusTrapStack.length > 0) {
            const lastTrap = this.focusTrapStack[this.focusTrapStack.length - 1];
            try {
                lastTrap?.deactivate();
            } catch (error) {
                cError('Focus trap deactivation failed while stacking:', error);
            }
        }

        // Create focus trap (replaces native dialog focus trapping)
        // Note: We push to stack BEFORE activation to maintain stack consistency.
        // If activation fails, the trap is still in the stack but inactive,
        // which is safe because deactivate() handles inactive traps gracefully.
        const trap = createFocusTrap($modal.get(0) as HTMLElement, {
            allowOutsideClick: true,
            preventScroll: true,
            // We handle ESC manually in onCaptureKeyup, so disable focus-trap's ESC handling
            escapeDeactivates: false,
            // We handle focus restoration manually in closeModal
            returnFocusOnDeactivate: false,
            // Fallback focus ensures trap can activate even when modal content is empty
            // (e.g., async modal before content loads). Falls back to close button or modal itself.
            fallbackFocus: () => closeButton || ($modal.get(0) as HTMLElement),
        });
        this.focusTrapStack.push(trap);

        // Delay focus trap activation until next frame to ensure:
        // 1. DOM elements are fully rendered and focusable
        // 2. CSS transitions have started (elements are visible)
        // 3. Browser differences in rendering timing are handled
        // Wrapped in try-catch to prevent focus trap errors from breaking modal functionality
        requestAnimationFrame(() => {
            try {
                trap.activate();
            } catch (error) {
                cError('Focus trap activation failed:', error);
            }
        });

        // Focus search input for search modal
        if ($modal.data('modalName') === 'search') {
            setTimeout(() => {
                $modal.find('.js-search-field').get(0)?.focus();
            }, 100);
        }

        // Attach scroll listener to modal element (the scrollable container in KAAN architecture)
        $modal.get(0)?.addEventListener('scroll', this.modalScrollPositionCallback, {passive: true});

        // Publish opened event
        publish('modal.opened', {
            el: $modal.get(0),
            type: $modal.data('modalType'),
            name: $modal.data('modalName'),
            isAsync: this.isAsyncModal($modal),
        } as ModalOpenedEvent);
    }

    /**
     * Triggers an event to replace the content of the current modal
     */
    protected replaceModal($modal: Cash): void {
        // Publish opened event
        publish('modal.replaced', {
            el: $modal.get(0),
            type: $modal.data('modalType'),
            name: $modal.data('modalName'),
            isAsync: this.isAsyncModal($modal),
        } as ModalReplacedEvent);
    }

    /**
     * Returns the content promise for the given URL
     */
    protected getAsyncModalContent(url: string, context?: string): Promise<AxiosResponse<string>> {
        const cleanUrl = getCleanUrl(url);
        const requestUrl = /ac=modal/.test(url)  ? cleanUrl : `${cleanUrl}${getQueryStringToken(cleanUrl)}ac=modal`;

        // Check if promise is already in cache
        if (requestUrl in this.contentPromises) {
            return this.contentPromises[requestUrl];
        }

        // Cache content that was requested via preload
        if (context === 'preload') {
            this.contentPromises[requestUrl] = ajaxGet(requestUrl);

            return this.contentPromises[requestUrl];
        }

        return ajaxGet(requestUrl);
    }

    /**
     * Handles the click event for open modal triggers
     * Based on the information of the click target an inline modal or a new page modal is opened
     */
    protected onModalOpenClick(event: PointerEvent): void {
        event.preventDefault();

        const $target = $(event.currentTarget as HTMLElement);
        const modalTarget = $target.data('modalTarget');
        const $modal = modalTarget
            ? $(this.ui.modal).filter(`[data-modal-name="${modalTarget}"]`)
            : $(this.ui.modal).first();

        if (modalTarget) {
            historyPush({
                type: HistoryType.ModalInline,
                url: getCleanUrl(),
                hash: `modal:${modalTarget}`,
            });
        } else {
            this.openModal($modal);
        }
    }

    /**
     * Handles the modal close click request
     * Either from a button or the modal backdrop
     */
    protected onModalCloseClick(event: PointerEvent): void {
        event.preventDefault();

        const $target = $(event.currentTarget as HTMLElement);
        const $modal = $target.parents(this.ui.modal);

        this.requestModalClose($modal);
    }

    /**
     * Handles the modal link opening request
     * Either from a button or the modal backdrop
     */
    protected onModalParentCloseTrigger(event: PointerEvent): void {
        const $target = $(event.currentTarget as HTMLElement);
        const $modal = $target.parents(this.ui.modal);

        this.requestModalClose($modal);
    }

    /**
     * Captures the keyup event from the browser
     * If modal is opened and ESC is pressed, we will close the current modal
     */
    protected onCaptureKeyup(event: KeyboardEvent): void {
        // Skip, if a photoswipe lightbox is opened
        if ($('.pswp--open').length > 0) {
            return;
        }

        const $searchModal = $(this.ui.searchModal);

        // Handle cmd + k to open search
        if ((event.metaKey || event.ctrlKey) && event.key === 'k' || event.key === 'K') {
            if (!this.modalOpened) {
                event.preventDefault();

                this.openModal($searchModal);
            }
            // Handle close request
        } else if (event.key === 'Escape') {
            if (!this.modalOpened) {
                return;
            }

            event.preventDefault();
            this.requestModalClose();
        }
    }

    /**
     * Requests a modal to be closed
     */
    protected requestModalClose($modal?: Cash): void {
        let $relatedModal = $modal ?? this.getActiveModal();

        if (history.state?.type === HistoryType.ModalInline && history.state.hash) {
            history.go(-1);
        } else if (history.state?.type === HistoryType.ModalAsync) {
            const level = Number($relatedModal.data('modalLevel') ?? 1);
            let count = 1;
            if (history.state?.modalHistoryCount?.length > 0) {
                count = history.state?.modalHistoryCount[level - 1];
            }

            history.go(count * (-1));
        } else {
            this.closeModal($relatedModal);
        }
    }

    /**
     * Closes the given modal
     * Uses class-based visibility instead of native dialog close()
     */
    protected async closeModal($modal: Cash): Promise<void> {
        const modal = $modal.get(0) as HTMLElement;

        const closeModalCallback = () => {
            // Scroll modal back to top (use modal element, not .modal__inner which doesn't exist)
            scrollToInstant($modal.get(0), {top: 0});

            // Set modal opened state to prevent double scrollbars
            setTimeout(() => {
                // Remove main modal class if appropriate
                this.modalOpened = $(this.ui.activeModal).length > 0;

                // Remove scrolling class
                $modal.removeClass(SCROLLABLE_CLASS);

                // Remove modal content after close for async modals
                if (this.isAsyncModal($modal)) {
                    $modal.find('.modal__body').html('');
                }
            }, 1);

            // Deactivate and remove focus trap for this modal
            // Wrapped in try-catch because deactivation can fail if the trap's container
            // was removed from DOM or if the trap was never successfully activated
            if (this.focusTrapStack.length > 0) {
                const trap = this.focusTrapStack.pop();
                try {
                    trap?.deactivate();
                } catch (error) {
                    cError('Focus trap deactivation failed:', error);
                }
            }

            // Re-enable previous trap if stacked modals remain (e.g., L1 modal under L2)
            // This can also fail if the previous modal's DOM state changed while it was inactive
            if (this.focusTrapStack.length > 0) {
                const previousTrap = this.focusTrapStack[this.focusTrapStack.length - 1];
                try {
                    previousTrap?.activate();
                } catch (error) {
                    cError('Focus trap re-activation failed:', error);
                }
            }

            // Re-focus last active element
            if (this.lastActiveElementStack.length > 0) {
                const previousActiveElement = this.lastActiveElementStack.pop() as HTMLElement;
                if (previousActiveElement && 'focus' in previousActiveElement) {
                    setTimeout(() => {
                        // Prevent scroll jump when restoring focus
                        previousActiveElement?.focus({preventScroll: true});
                    }, 100);
                }
            }
        };

        // Add transition end listener to do some cleanup work
        const transitionPromise = new Promise<void>((resolve) => {
            $modal.find(this.ui.modalContent).one('transitionend', () => {
                resolve();
            });
        });

        // Execute modal cleanup after transition or timeout
        Promise.any([
            transitionPromise,
            wait(400),
        ]).then(closeModalCallback);

        // Close modal (replaces native close())
        $modal.removeClass(ACTIVE_CLASS);

        // Remove modal from stack
        this.modalStack = this.modalStack.filter((element) => {
            return element !== modal;
        });

        // Remove body class
        if ($modal.data('modalName')) {
            this.$body.removeClass(`has-modal-${$modal.data('modalName')}`);
        }

        // Remove scroll listener from modal element (must match what we added in openModal)
        $modal.get(0)?.removeEventListener('scroll', this.modalScrollPositionCallback);

        // Publish close event
        publish('modal.closed', {
            el: modal,
            type: $modal.data('modalType'),
            name: $modal.data('modalName'),
            level: $modal.data('modalLevel'),
            isAsync: this.isAsyncModal($modal),
            hasActiveModal: this.modalOpened,
        } as ModalClosedEvent);
    }

    /**
     * Stores the current scroll position for a modal
     * Uses modalLevel:modalUrl as key to track position per modal level independently
     */
    protected modalScrollPositionHandler(event: Event): void {
        if (event.target && event.target instanceof HTMLElement) {
            const scrollTop = event.target.scrollTop;

            // Find the modal element from the scroll target
            const $modal = $(event.target).closest('.js-modal, .js-modal-async');
            if ($modal.length === 0) {
                return;
            }

            // Store scroll position using modal level + URL as key
            const modalLevel = $modal.data('modalLevel') ?? 1;
            const modalUrl = history.state?.modalUrl?.[modalLevel - 1];

            if (modalUrl) {
                const scrollKey = `${modalLevel}:${modalUrl}`;
                this.modalScrollPosition[scrollKey] = scrollTop;
            }
        }
    }

    /**
     * Handles focus trap activate/deactivate requests from external components (e.g., PhotoSwipe lightbox)
     * This allows lightboxes to temporarily take over focus management while open,
     * then restore the modal's focus trap when closed.
     *
     * Flow:
     * 1. Lightbox opens → publishes 'modal.focustrap.deactivate' → we deactivate modal's trap
     * 2. Lightbox closes → publishes 'modal.focustrap.activate' → we re-activate modal's trap
     *
     * All operations are wrapped in try-catch because the modal's DOM state may have changed
     * while the lightbox was open (e.g., async content replacement).
     */
    protected onFocustrapChangeRequest(topic: string): void {
        const shouldDeactivateTrap = topic === 'modal.focustrap.deactivate';

        if (this.focusTrapStack.length === 0) {
            return;
        }

        if (shouldDeactivateTrap) {
            // Find and store the currently active trap's index so we can restore it later
            this.lastActiveFocusTrapIndex = this.focusTrapStack.findIndex((item: FocusTrap) => item.active);
            if (this.lastActiveFocusTrapIndex >= 0) {
                try {
                    this.focusTrapStack[this.lastActiveFocusTrapIndex].deactivate();
                } catch (error) {
                    cError('Focus trap deactivation failed (lightbox request):', error);
                }
            }
        } else if (typeof this.lastActiveFocusTrapIndex === 'number' && this.lastActiveFocusTrapIndex >= 0) {
            // Re-activate the previously active trap when lightbox closes
            try {
                this.focusTrapStack[this.lastActiveFocusTrapIndex].activate();
            } catch (error) {
                cError('Focus trap re-activation failed (lightbox closed):', error);
            }
            this.lastActiveFocusTrapIndex = null;
        }
    }

    /**
     * Returns the modal Cash HtmlElement for the given hash or undefined, if modal was not found for hash
     */
    protected getModalFromHash(hash?: string): Cash|undefined {
        if (!hash) {
            return undefined;
        }

        if (!(/^modal\:.+/i.test(hash))) {
            return undefined;
        }

        const $modal = $(this.ui.modal).filter(`[data-modal-name="${hash.split(':')[1]}"]`);

        return $modal.length > 0 ? $modal : undefined;
    }

    /**
     * Returns the current active modal
     */
    protected getActiveModal(): Cash {
        return this.modalStack.length > 0
            ? $(this.modalStack[this.modalStack.length - 1])
            : $(this.ui.activeModal);
    }

    /**
     * Indicates, if a modal is an async modal
     */
    protected isAsyncModal($modal: Cash): boolean {
        return $modal.filter(this.ui.asyncModal).length > 0;
    }

    /**
     * Indicates if the given (primary) state is relevant for inline modals
     */
    protected isInlineModalRelevantPrimaryState(state: Partial<InternalHistoryState>, context?: string): boolean {
        return History.isStateOfType(state, HistoryType.ModalInline, context);
    }

    /**
     * Indicates if the given state is relevant for inline modals
     */
    protected isInlineModalRelevantState(state: Partial<InternalHistoryState>, context?: string): boolean {
        return History.isStateOfType(state, HistoryType.ModalInline);
    }

    /**
     * Indicates if the given (primary) state is relevant for inline modals
     */
    protected isAsyncModalRelevantPrimaryState(state: Partial<InternalHistoryState>, context?: string): boolean {
        return History.isStateOfType(state, HistoryType.ModalAsync, context);
    }

    /**
     * Indicates if the given state is relevant for inline modals
     */
    protected isAsyncModalRelevantState(state: Partial<InternalHistoryState>, context?: string): boolean {
        return History.isStateOfType(state, HistoryType.ModalAsync);
    }
}
